/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        i18n Type Definitions & Locale Metadata (Task 10.7.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import en from './locales/en.json';

export const DEFAULT_LOCALE = 'en';

export const SUPPORTED_LOCALES = ['en', 'vi', 'ja', 'ko', 'es', 'zh'] as const;

export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export interface LocaleMetadata {
  code: SupportedLocale;
  name: string;
  nativeName: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

export const LOCALE_CONFIG: Record<SupportedLocale, LocaleMetadata> = {
  en: { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸', dir: 'ltr' },
  vi: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
  ja: { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', dir: 'ltr' },
  ko: { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', dir: 'ltr' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', dir: 'ltr' },
  zh: { code: 'zh', name: 'Chinese', nativeName: '简体中文', flag: '🇨🇳', dir: 'ltr' },
};

export type TranslationSchema = typeof en;

// Type-safe recursive key paths (e.g. 'common.save' | 'wizard.step1')
type DotPrefix<T extends string> = T extends '' ? '' : `.${T}`;

export type DotNestedKeys<T> = (
  T extends object
    ? { [K in Exclude<keyof T, symbol>]: `${K}${DotPrefix<DotNestedKeys<T[K]>>}` }[Exclude<keyof T, symbol>]
    : ''
) extends infer D
  ? Extract<D, string>
  : never;

export type TranslationKey = DotNestedKeys<TranslationSchema>;

export type TranslationParams = Record<string, string | number>;
